<template>
  <!-- `.w-tw` is required for Zone Tailwind (`important: '.w-tw'`) and Material Symbols on the icons. -->
  <div class="w-tw flex flex-col gap-2" data-testid="ua-feedback">
    <div v-if="!sent" class="flex items-center gap-1">
      <span class="mr-1 text-sm text-gray-500">Want to see more like this in the future?</span>
      <button
        v-for="option in ratingOptions"
        :key="option.value"
        type="button"
        class="flex size-8 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100"
        :class="{ 'bg-primary-100 text-primary-600': rating === option.value }"
        :aria-label="option.label"
        :aria-pressed="rating === option.value"
        @click="rate(option.value)"
      ><ZoneIcon :icon="option.icon" size="lg" aria-hidden="true" /></button>
    </div>
    <div
      v-if="open && !sent"
      class="flex w-full flex-col gap-2.5 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3"
      data-testid="feedback-form"
    >
      <span class="text-xs font-medium text-gray-600">{{ reasonTitle }}</span>
      <div class="flex flex-wrap items-center gap-2">
        <button
          v-for="reason in reasons[rating]"
          :key="reason.code"
          type="button"
          class="inline-flex h-7 select-none items-center whitespace-nowrap rounded-full border border-gray-300 bg-white px-3 text-sm font-regular text-gray-700 hover:border-primary-500 hover:text-primary-600 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-primary-700"
          data-testid="feedback-reason"
          @click="submit({ reason_code: reason.code })"
        >{{ reason.label }}</button>
      </div>
      <div class="flex items-center gap-2 rounded-lg border border-gray-300 bg-white p-1 pl-3 focus-within:border-primary-500">
        <input
          v-model="text"
          type="text"
          maxlength="2000"
          placeholder="Or tell us in your own words"
          aria-label="Or tell us in your own words"
          class="h-7 min-w-0 flex-1 border-0 bg-transparent p-0 text-sm font-regular text-gray-700 outline-none placeholder:font-regular focus:border-0 focus:ring-0"
          @keydown.enter="submitOnEnter"
        >
        <ZoneButton
          icon-only
          size-class="size-7 p-0"
          class="flex-none aria-disabled:!border-gray-100 aria-disabled:!bg-gray-100 aria-disabled:!text-gray-400 aria-disabled:!opacity-100"
          aria-label="Send"
          data-testid="feedback-send"
          :disabled="!text.trim()"
          @click="submitText"
        >
          <ZoneIcon icon="arrow_forward" size="lg" class="!text-base" aria-hidden="true" />
        </ZoneButton>
      </div>
      <div>
        <ZoneButton variant="ghost" color="gray" data-testid="feedback-skip" @click="submit()">Skip</ZoneButton>
      </div>
    </div>
    <p v-if="sent" class="m-0 text-xs text-gray-500">Thanks for the feedback.</p>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import ZoneButton from 'zone/components/base/Button.vue';
import ZoneIcon from 'zone/components/base/Icon.vue';
import { UnusualActivitiesResource } from '~/resources';

// Rates one Unusual Activity item (HUB-19218). The server saves the label and sends the
// `Label Started` / `Label Submitted` Segment events, see UnusualActivityLabels::RateAction.
const props = defineProps({
  organizationId: { type: [Number, String], required: true },
  targetUserId: { type: [Number, String], required: true },
  date: { type: String, required: true },
  timeZone: { type: String, default: null },
  item: { type: Object, required: true },
  itemLabel: { type: String, default: '' },
});
// `saved` carries the stored label up, so the dialog keeps it when this component is re-created for the item.
const emit = defineEmits(['saved']);

const ratingOptions = [
  { value: 'up', icon: 'thumb_up', label: 'Yes, show more like this' },
  { value: 'down', icon: 'thumb_down', label: 'No, show less like this' },
];
const reasons = {
  up: [
    { code: 'exactly_what_i_needed', label: 'This was exactly what I was looking for' },
    { code: 'useful_missing_info', label: 'Useful, but some information is missing' },
  ],
  down: [
    { code: 'normal_for_person', label: 'Normal for this person' },
    { code: 'normal_for_work', label: 'Normal for this kind of work' },
    { code: 'often_unclear_action', label: 'I see it often, but I don\'t know what to do about it' },
    { code: 'not_looked_for', label: 'Not something we look for' },
  ],
};

// A stored rating stays highlighted after a reload; clicking a thumb again starts a new answer.
const rating = ref(props.item.label?.rating ?? null);
const open = ref(false);
const sent = ref(false);
const text = ref('');
let started = null;

const reasonTitle = computed(() => (rating.value === 'up'
  ? 'What made this useful?'
  : `Why don't you want to see ${props.itemLabel ? `"${props.itemLabel}"` : 'this'}?`));

const urlParams = () => ({ organizationId: props.organizationId });

const rate = (value) => {
  rating.value = value;
  open.value = true;
  const ratedAt = new Date().toISOString();
  const send = () => UnusualActivitiesResource.rateLabel(urlParams(), {
    target_user_id: props.targetUserId,
    date: props.date,
    record_id: props.item.id,
    rating: value,
    rated_at: ratedAt,
    time_zone: props.timeZone,
  }).then((response) => {
    emit('saved', { rating: value, label_id: response.label_id, submitted: false });

    return response;
  });
  // A click made while the previous one is still saving waits for it, so the server stores them in click order.
  const request = (started ? started.then(send) : send())
    .catch(() => {
      // The api layer already shows the error; the rating is undone, unless a newer click replaced it.
      if (started === request) {
        rating.value = props.item.label?.rating ?? null;
        open.value = false;
        started = null;
      }

      return null;
    });

  started = request;
};

const submit = async ({ reason_code = null, reason_text = null } = {}) => {
  if (sent.value || !started) {return;}
  sent.value = true;
  const response = await started;

  if (!response) {
    sent.value = false;

    return;
  }
  // The api layer shows the error; the prompt comes back so the answer can be sent again.
  await UnusualActivitiesResource.submitLabel({ ...urlParams(), labelId: response.label_id }, { reason_code, reason_text })
    .then(({ label_id }) => emit('saved', { rating: rating.value, label_id, submitted: true }))
    .catch(() => {
      sent.value = false;
    });
};

const submitText = () => {
  if (!text.value.trim()) {return;}
  submit({ reason_text: text.value });
};

const submitOnEnter = (event) => {
  if (event.isComposing || event.keyCode === 229) {return;}
  submitText();
};
</script>
